import { useEffect, type ReactNode } from "react";
import { B2B_APPLICATION_LIMITS } from "@kalori/business/applications";
import { B2B_BRAND_LOGO_HEIGHT, B2B_ONBOARDING_PRODUCTS, onboardingChoicesClass, onboardingFieldLabel, onboardingFooterLinks } from "@kalori/business/onboarding";
import { activeB2BApplyOptions, type B2BApplyQuestion } from "@kalori/business/applications/client";
import { KaloriLogo } from "../KaloriLogo";

/** HTML の枠とクラスは SSR の renderB2BOnboarding と共通。CSS は同じ正本から外部アセットにする。 */
export function OnboardingLayout({ title, children }: { title: string; children: ReactNode }) {
  const product = B2B_ONBOARDING_PRODUCTS.merchant;
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | ${product.name}`;
    return () => { document.title = previousTitle; };
  }, [title, product.name]);
  return (
    <main className="b2b-onboarding">
      <section className="b2b-card">
        <a className="b2b-brand" href={product.lpUrl}><KaloriLogo height={B2B_BRAND_LOGO_HEIGHT} label="Kalori" /><span>{product.suffix}</span></a>
        <h1>{title}</h1>
        {children}
      </section>
      <footer className="b2b-footer" aria-label="サポート">
        {onboardingFooterLinks("merchant").map((link) => <a key={link.href} href={link.href}>{link.label}</a>)}
        <span>© kalbi合同会社</span>
      </footer>
    </main>
  );
}

export function FieldLabel({ label, required = false }: { label: string; required?: boolean }) {
  return <span className="b2b-label">{onboardingFieldLabel(label, required)}{required && <span className="b2b-required">必須</span>}</span>;
}

export function ApplyQuestion({ question: q, value, onChange }: { question: B2BApplyQuestion; value: string; onChange: (value: string) => void }) {
  const hintId = `b2b-${q.key}-hint`;
  if (!q.options) return (
    <label className="b2b-field">
      <FieldLabel label={q.label} required={q.required} />
      <input value={value} required={q.required} maxLength={B2B_APPLICATION_LIMITS.attrValue} placeholder={q.placeholder} onChange={(e) => onChange(e.target.value)} aria-describedby={q.hint ? hintId : undefined} />
      {q.hint && <span className="b2b-hint b2b-hint-after" id={hintId}>{q.hint}</span>}
    </label>
  );
  return (
    <fieldset className="b2b-field" aria-describedby={q.hint ? hintId : undefined}>
      <legend className="b2b-label">{onboardingFieldLabel(q.label, q.required)}{q.required && <span className="b2b-required">必須</span>}</legend>
      {q.hint && <p className="b2b-hint" id={hintId}>{q.hint}</p>}
      <div className={onboardingChoicesClass(q)}>
        {activeB2BApplyOptions(q.options).map((o) => (
          <label key={o.value} className="b2b-choice">
            <input type="radio" name={q.key} value={o.value} required={q.required} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
